import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  FieldError,
  Fieldset,
  FlexRow,
  Form,
  IconGlobeAlt,
  IconInstagram,
  IconTwitter,
  Input,
  InputPre,
  Label,
  Link,
  RequiredText,
  RichTextEditor,
  Select,
  UnexpectedError,
} from "atoms";
import { ImageUpload } from "features/images";
import { AffiliatedArtistInput } from "features/search";
import { useAuth } from "features/auth";
import { fieldOptions, validators } from "utils/form";
import { useCollectiveForm } from "../api/useCollectiveForm";
import type { Collective } from "../types";

type CollectiveFormProps = {
  collective?: Collective;
  redirectPath: (collectiveId: string) => string;
};

const linkInputs = [
  [
    "Personal Website",
    "website",
    "https://your-website.com",
    <IconGlobeAlt aria-hidden />,
  ],
  [
    "Instagram URL",
    "instagram",
    "https://instagram.com/your-username",
    <IconInstagram aria-hidden />,
  ],
  [
    "Twitter URL",
    "twitter",
    "https://twitter.com/your-username",
    <IconTwitter aria-hidden />,
  ],
] as const;

const submitLabel = (isEdit: boolean, forcePublish: boolean) => {
  if (forcePublish) {
    return "Publish Collective";
  }
  if (isEdit) {
    return "Update Collective";
  }
  return "Create Collective";
};

function CollectiveForm({ collective, redirectPath }: CollectiveFormProps) {
  const navigate = useNavigate();

  const {
    roles: { showAdminFeatures },
  } = useAuth();

  const {
    control,
    formState: { errors },
    isEdit,
    mutation,
    onSubmit,
    register,
  } = useCollectiveForm({
    collective,
    onSuccess: (newCollective) => navigate(redirectPath(newCollective.id)),
  });

  const cancelPath = redirectPath(collective?.id ?? "");

  // DRAFT PROFILES - If the user is a collective admin (not a HUG admin)
  // then show a PUBLISH button instead of a UPDATE button. We use a hidden
  // field to indicate forcePublish to useCollectiveForm.
  const forcePublish = collective?.status !== "PUBLISHED" && !showAdminFeatures;

  return (
    <Form onSubmit={onSubmit}>
      {showAdminFeatures && (
        <Label>
          Collective Slug
          <RequiredText />
          <Input
            {...register("id", {
              ...fieldOptions.required,
              validate: validators.validSlug,
            })}
            disabled={isEdit}
          />
          {errors.id && <FieldError>{errors.id.message}</FieldError>}
        </Label>
      )}

      <Label>
        Collective Name
        <RequiredText />
        <Input
          {...register("name", {
            ...fieldOptions.required,
            ...fieldOptions.maxLength(80),
          })}
        />
        {errors.name && <FieldError>{errors.name.message}</FieldError>}
      </Label>

      {showAdminFeatures && (
        <Checkbox
          {...register("hasCollectivePage")}
          label={<strong>Has collective page</strong>}
        />
      )}

      {showAdminFeatures && (
        <Label>
          Collective Page Status
          <RequiredText />
          <Select {...register("status", { ...fieldOptions.required })}>
            <option value="PUBLISHED">Published</option>
            <option value="DRAFT">Draft</option>
            <option value="ARCHIVED">Archived</option>
          </Select>
        </Label>
      )}

      <Label>
        Description
        <RequiredText />
        <RichTextEditor control={control} name="description" required />
        {errors.description && (
          <FieldError>{errors.description.message}</FieldError>
        )}
      </Label>

      <ImageUpload
        control={control}
        fieldName="badge"
        src={collective?.badge}
        aspect="square"
        label={
          <>
            Badge
            <RequiredText />
          </>
        }
        errorMessage={errors.badge?.url?.message}
        required
      />

      <ImageUpload
        control={control}
        fieldName="banner"
        src={collective?.banner}
        aspect="banner"
        label="Banner"
        errorMessage={errors.banner?.url?.message}
      />

      {linkInputs.map(([label, type, placeholder, icon], index) => (
        <Label key={`${type}-${label}`}>
          {label}
          <FlexRow gap="0">
            <InputPre>{icon}</InputPre>
            <Input
              {...register(`links.${index}.url`, {
                validate: (val: string) => !val || validators.validURL(val),
              })}
              placeholder={placeholder}
              autoCapitalize="off"
            />
          </FlexRow>
          <Input
            type="hidden"
            {...register(`links.${index}.type`)}
            value={type}
          />
          {errors.links?.[index]?.url && (
            <FieldError>{errors.links?.[index]?.url?.message}</FieldError>
          )}
        </Label>
      ))}

      <Fieldset id="manage-artists">
        {isEdit ? (
          <span>Add and Remove Artists</span>
        ) : (
          <span>Add Artists</span>
        )}
        <AffiliatedArtistInput
          control={control}
          name="affiliatedArtists"
          typeLabel="Artists"
        />
      </Fieldset>

      {forcePublish && (
        <Input type="hidden" {...register("forcePublish")} value="TRUE" />
      )}

      {!!mutation.error && <UnexpectedError error={mutation.error} />}
      <div>
        <br />
        <FlexRow justifyContent="space-between">
          <Link variant="secondary" to={cancelPath}>
            Cancel
          </Link>
          <Button disabled={mutation.isLoading} type="submit">
            {submitLabel(isEdit, forcePublish)}
          </Button>
        </FlexRow>
      </div>
    </Form>
  );
}

export { CollectiveForm };
